import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Pagination,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createKnowledgeApi } from "../api";
import {
  formatLabel,
  policyStatusTone,
} from "../labels";
import { useKnowledgeQueue } from "../useKnowledgeQueue";
import type { PolicyDto } from "../types";
import { CreatePolicyDialog } from "../components/KnowledgeDialogs";

const POLICY_STATUSES = [
  "draft",
  "under_review",
  "approved",
  "active",
  "superseded",
  "retired",
] as const;

const DEPARTMENTS = [
  "system_administration",
  "operations",
  "finance",
  "customer_support",
  "trust_and_safety",
  "marketing",
  "creator_operations",
  "marketplace_health",
] as const;

function QueueShell({
  title,
  description,
  breadcrumbs,
  filters,
  actions,
  error,
  loading,
  empty,
  children,
  page,
  nextCursor,
  onNext,
  onPrevious,
  onRefresh,
}: {
  title: string;
  description: string;
  breadcrumbs: { label: string; to?: string }[];
  filters: ReactNode;
  actions?: ReactNode;
  error: string | null;
  loading: boolean;
  empty: boolean;
  children: ReactNode;
  page: number;
  nextCursor: string | null;
  onNext: () => void;
  onPrevious: () => void;
  onRefresh: () => void;
}) {
  usePageBreadcrumbs(breadcrumbs);

  return (
    <Stack gap={24}>
      <SectionHeader
        title={title}
        description={description}
        actions={
          <Flex gap={8} wrap>
            {actions}
            <Button variant="secondary" onClick={onRefresh}>
              Refresh
            </Button>
          </Flex>
        }
      />
      <Filters>{filters}</Filters>
      {error ? (
        <Alert tone="danger" title="Load failed">
          {error}
        </Alert>
      ) : null}
      {loading ? <LoadingState message={`Loading ${title}…`} /> : null}
      {!loading && !error && empty ? (
        <EmptyState
          title={`No ${title.toLowerCase()}`}
          description="Adjust filters or create a draft."
          reason="none"
        />
      ) : null}
      {!loading && !empty ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Page {page}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) onNext();
                  if (nextPage < page) onPrevious();
                }}
              />
            }
          />
          {children}
        </Stack>
      ) : null}
    </Stack>
  );
}

export function PoliciesQueuePage() {
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createKnowledgeApi(client), [client]);
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadPage = useMemo(
    () =>
      async (params: {
        cursor?: string;
        status: string;
        sort: string;
        extra: Record<string, string>;
      }) => {
        const list = await api.listPolicies({
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: 25,
          ...(params.status ? { status: params.status } : {}),
          ...(params.sort ? { sort: params.sort } : {}),
          ...(params.extra.owning_department
            ? { owning_department: params.extra.owning_department }
            : {}),
          ...(params.extra.owner_user_id
            ? { owner_user_id: params.extra.owner_user_id }
            : {}),
          ...(params.extra.policy_key
            ? { policy_key: params.extra.policy_key }
            : {}),
        });
        return {
          items: list.items,
          nextCursor: list.next_cursor ?? null,
        };
      },
    [api],
  );

  const queue = useKnowledgeQueue<PolicyDto>(loadPage, { sort: "id" });

  return (
    <>
      <QueueShell
        title="Policies"
        description="Policy records with version history on the detail page. Activate/publish-equivalent requires a version."
        breadcrumbs={[
          { label: "HUDDL HQ", to: "/app" },
          { label: "Knowledge Base", to: "/app/knowledge" },
          { label: "Policies" },
        ]}
        actions={
          <Button onClick={() => setCreateOpen(true)}>Create policy</Button>
        }
        filters={
          <Flex gap={12} wrap>
            <Select
              label="Status"
              value={queue.status}
              onChange={(event) => queue.setStatus(event.target.value)}
              options={[
                { value: "", label: "All statuses" },
                ...POLICY_STATUSES.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Select
              label="Department"
              value={queue.extra.owning_department ?? ""}
              onChange={(event) =>
                queue.setExtra("owning_department", event.target.value)
              }
              options={[
                { value: "", label: "All departments" },
                ...DEPARTMENTS.map((value) => ({
                  value,
                  label: formatLabel(value),
                })),
              ]}
            />
            <Input
              label="Policy key"
              value={queue.extra.policy_key ?? ""}
              onChange={(event) =>
                queue.setExtra("policy_key", event.target.value)
              }
            />
            <Input
              label="Owner User ID"
              value={queue.extra.owner_user_id ?? ""}
              onChange={(event) =>
                queue.setExtra("owner_user_id", event.target.value)
              }
            />
            <Select
              label="Sort"
              value={queue.sort}
              onChange={(event) => queue.setSort(event.target.value)}
              options={[
                { value: "id", label: "ID" },
                { value: "status", label: "Status" },
                { value: "policy_key", label: "Policy key" },
              ]}
            />
            <Button variant="secondary" onClick={() => queue.clearFilters()}>
              Reset
            </Button>
          </Flex>
        }
        error={queue.error}
        loading={queue.loading}
        empty={queue.rows.length === 0}
        page={queue.page}
        nextCursor={queue.nextCursor}
        onNext={() => queue.goNext()}
        onPrevious={() => queue.goPrevious()}
        onRefresh={() => void queue.load()}
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Key</Th>
              <Th>Status</Th>
              <Th>Department</Th>
              <Th>Owner</Th>
              <Th>Current version</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.rows.map((row) => (
              <Tr
                key={row.id}
                onClick={() =>
                  void navigate(`/app/knowledge/policies/${row.id}`)
                }
              >
                <Td>{row.policy_key}</Td>
                <Td>
                  <StatusChip tone={policyStatusTone(row.status)}>
                    {formatLabel(row.status)}
                  </StatusChip>
                </Td>
                <Td>{formatLabel(row.owning_department)}</Td>
                <Td>{row.owner_user_id ?? "—"}</Td>
                <Td>{row.current_version_id ?? "—"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </QueueShell>

      <CreatePolicyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={createBusy}
        error={createError}
        onSubmit={async (body) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.createPolicy(body);
            toast.push({ title: "Policy created", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/knowledge/policies/${result.policy.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </>
  );
}
